import type { RenewalOffer } from '@/lib/types/billing';
import type { DataRoomEntitlement, RefusalReason } from './api/data-room.api';
import { COMMAND_CENTER_SERVICES as S } from './constants';

/**
 * Where the viewer stands, for the services under the top panel:
 * - `package`: Premium or Subscription open (a Sr. Next client);
 * - `renewal`: the package ended and the renewal offer is open to them (the
 *   Issue Report is still on sale: FR-295, a former Premium startup "still
 *   sees the Issue Report offers like any other startup");
 * - `issueReport`: an Issue Report open;
 * - `blocked`: the startup has access this person cannot use (no seat,
 *   company email, suspended...), or it is still loading, so nothing to sell;
 * - `open`: nothing bought, or it ended: the buy offers apply.
 */
export type CommandCenterState = 'package' | 'renewal' | 'issueReport' | 'blocked' | 'open';

export type ServiceCtaSpec =
  | { kind: 'buy' }
  | { kind: 'schedule' }
  | { kind: 'open'; label: string }
  | { kind: 'status'; label: string }
  | { kind: 'none' };

export interface CommandCenterCtas {
  issue: ServiceCtaSpec;
  seed: ServiceCtaSpec;
  workspaces: ServiceCtaSpec;
  compareLeft: ServiceCtaSpec;
  /** The "choose your next step" comparison is pointless to someone already on the package. */
  showCompare: boolean;
}

/** Refusals that mean "nothing live": the buy offers apply to these alone. */
const NOTHING_LIVE = new Set<RefusalReason>(['no_active_service', 'entitlement_expired']);

/**
 * Who may start a purchase here:
 * - `buy`: a startup's billing manager, or someone with no organisation at all
 *   (the journey creates their startup);
 * - `askAdmin`: anyone else at a startup;
 * - `none`: an investor or partner, whom the journey turns away (admin_only).
 */
export type BuyAccess = 'buy' | 'askAdmin' | 'none';

const BUY: ServiceCtaSpec = { kind: 'buy' };
const SCHEDULE: ServiceCtaSpec = { kind: 'schedule' };
const NONE: ServiceCtaSpec = { kind: 'none' };
const status = (label: string): ServiceCtaSpec => ({ kind: 'status', label });

/** One row per state, so the rules read as the table they were agreed as. */
const CTAS: Record<CommandCenterState, CommandCenterCtas> = {
  package: {
    issue: status(S.INCLUDED),
    seed: status(S.CURRENT_PACKAGE),
    workspaces: { kind: 'open', label: S.OPEN_DATA_ROOM },
    compareLeft: NONE,
    showCompare: false,
  },
  renewal: {
    issue: BUY,
    seed: status(S.PREVIOUS_PACKAGE),
    workspaces: status(S.REOPENS_ON_RENEW),
    compareLeft: BUY,
    showCompare: true,
  },
  issueReport: {
    issue: { kind: 'open', label: S.OPEN_ISSUE_REPORT },
    seed: SCHEDULE,
    workspaces: status(S.WITH_PACKAGE),
    compareLeft: status(S.YOU_HAVE_THIS),
    showCompare: true,
  },
  blocked: { issue: NONE, seed: SCHEDULE, workspaces: NONE, compareLeft: NONE, showCompare: false },
  open: {
    issue: BUY,
    seed: SCHEDULE,
    workspaces: status(S.WITH_PACKAGE),
    compareLeft: BUY,
    showCompare: true,
  },
};

/**
 * Work out the viewer's state from the backend's entitlement answer and the
 * renewal offer.
 *
 * @param entitlement - the startup's entitlement; undefined with no startup
 * @param renewal - the renewal offer; undefined when not fetched (not a buyer)
 * @returns the state the services' calls to action follow
 */
export function resolveCommandCenterState(
  entitlement: DataRoomEntitlement | undefined,
  renewal: RenewalOffer | undefined,
): CommandCenterState {
  if (!entitlement) return 'open';
  if (entitlement.entitled)
    return entitlement.service === 'issue_report' ? 'issueReport' : 'package';
  if (!NOTHING_LIVE.has(entitlement.reason)) return 'blocked';
  // Only `no_active_service` gets the renewal panel at the top
  // (CommandCenterTop), so only it may point the cards at renewal.
  const renewalOpen = renewal?.eligible === true && renewal.plans.length > 0;
  return entitlement.reason === 'no_active_service' && renewalOpen ? 'renewal' : 'open';
}

/**
 * The services' calls to action for a state. A Startup Member sees who can
 * buy instead of a buy button; an investor or partner sees no buy button,
 * since the purchase journey would only turn them away.
 *
 * @param state - from `resolveCommandCenterState`
 * @param buyAccess - who this viewer is as a buyer
 * @returns one call to action per service, and whether to show the comparison
 */
export function resolveCommandCenterCtas(
  state: CommandCenterState,
  buyAccess: BuyAccess,
): CommandCenterCtas {
  const ctas = CTAS[state];
  if (buyAccess === 'buy') return ctas;
  const instead = buyAccess === 'askAdmin' ? status(S.ASK_ADMIN) : NONE;
  const guard = (spec: ServiceCtaSpec) => (spec.kind === 'buy' ? instead : spec);
  return { ...ctas, issue: guard(ctas.issue), compareLeft: guard(ctas.compareLeft) };
}
